/**
 * Lightweight wrapper around gtag() for tracking custom events.
 * Only fires when GA is loaded (i.e. user consented to analytics cookies).
 *
 * Booking click tracking is handled by the TrackedBookingLink component,
 * NOT by a global listener. Phone clicks are still auto-tracked globally.
 */

declare global {
  interface Window {
    dataLayer: unknown[] | undefined;
  }
}

let hasInitializedGlobalClickTracking = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function gtag(..._args: any[]) {
  if (!window.dataLayer) return;
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
) {
  gtag("event", eventName, params);
}

// Pre-defined events
export const analytics = {
  contactFormSubmit: () =>
    trackEvent("generate_lead", { event_category: "contact", event_label: "contact_form" }),

  bookingClick: (location: string, placement: string = "inline") =>
    trackEvent("begin_checkout", { event_category: "booking", event_label: location, placement }),

  stickyCtaImpression: (page: string) =>
    trackEvent("sticky_cta_impression", { event_category: "booking", event_label: page, placement: "mobile_sticky" }),

  stickyCtaClick: (page: string) =>
    trackEvent("sticky_cta_click", { event_category: "booking", event_label: page, placement: "mobile_sticky" }),

  newsletterSignup: () =>
    trackEvent("sign_up", { event_category: "newsletter", method: "footer" }),

  whatsappClick: () =>
    trackEvent("contact", { event_category: "whatsapp", event_label: "floating_button" }),

  phoneClick: (number: string) =>
    trackEvent("contact", { event_category: "phone", event_label: number }),

  socialClick: (platform: string) =>
    trackEvent("social_click", { event_category: "social", event_label: platform }),

  sectionView: (sectionName: string, pageName: string) =>
    trackEvent("section_view", { event_category: "scroll_depth", event_label: sectionName, page_name: pageName }),

  resourceClick: (params: {
    title: string;
    type: string;
    action: "download" | "view_guide" | "external_link";
    destination: string;
    fileFormat?: string;
    page?: string;
  }) => {
    const eventName = params.action === "download" ? "file_download" : "select_content";
    trackEvent(eventName, {
      event_category: "resource",
      event_label: params.title,
      resource_title: params.title,
      resource_type: params.type,
      resource_action: params.action,
      resource_destination: params.destination,
      file_extension: params.fileFormat,
      file_name: params.action === "download" ? params.destination.split("/").pop() : undefined,
      content_type: "resource",
      page: params.page ?? "/resources",
    });
  },
};

/**
 * Global click listener that auto-tracks tel: phone link clicks.
 * Booking link tracking is handled by TrackedBookingLink component instead.
 */
export function initGlobalClickTracking() {
  if (hasInitializedGlobalClickTracking) return;
  hasInitializedGlobalClickTracking = true;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      let anchor: HTMLAnchorElement | null = null;

      if (target instanceof HTMLAnchorElement) {
        anchor = target;
      } else if (target instanceof Element) {
        const found = target.closest("a[href]");
        if (found instanceof HTMLAnchorElement) anchor = found;
      }

      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";

      if (href.startsWith("tel:")) {
        analytics.phoneClick(href.replace("tel:", ""));
      }
    },
    true,
  );
}
